<script>
import dayjs from 'dayjs';
import { pageTitleMixin } from '@ubic/vue-mixins';
import { formatDateTime } from '@/utils';
import { permissions } from '@/constants';
import IconGhost from '@/components/icons/Ghost';
import ErrorAccessDenied from '@/components/ErrorAccessDenied';
import DateFilter from './components/DateFilter';
import Filter from './components/Filter';
import ChangesModal from './components/ChangesModal';
import service from './service';
import { actionTypes, entityTypes } from './constants';

const mapActionTitle = {
  [actionTypes.login]: 'авторизовался ',
  [actionTypes.create]: 'добавил ',
  [actionTypes.update]: 'изменил ',
  [actionTypes.remove]: 'удалил ',
};

const mapEntityTitle = {
  [entityTypes.admin]: 'администратора ',
  [entityTypes.department]: 'департамент ',
  [entityTypes.log]: 'запись лога ',
  [entityTypes.project]: 'проект ',
  [entityTypes.settings]: 'настройки ',
  [entityTypes.weekends]: 'календарь за ',
  [entityTypes.worker]: 'сотрудника ',
};

export default {
  pageTitle: 'Журнал событий',
  mixins: [pageTitleMixin],
  data () {
    return {
      isFetching: false,
      items: [],
      detailsItem: null,
      filter: {
        minDate: dayjs()
          .startOf('day')
          .subtract(7, 'day')
          .format('YYYY-MM-DD'),
        maxDate: dayjs().startOf('day').format('YYYY-MM-DD'),
        adminId: '',
        actionType: '',
        entityType: '',
        entityId: '',
      },
    };
  },
  computed: {
    hasReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.JOURNAL_READ);
    },
    mapAdmins () {
      return this.$store.state.dict.admins;
    },
    mapDepartments () {
      return this.$store.state.dict.departments;
    },
    mapProjects () {
      return this.$store.state.dict.projects;
    },
    mapWorkers () {
      return this.$store.state.dict.workers;
    },
    canReadAdmin () {
      return this.$store
        .getters['auth/checkPermission'](permissions.ADMIN_READ);
    },
    canReadDepartment () {
      return this.$store
        .getters['auth/checkPermission'](permissions.DEPARTMENT_READ);
    },
    canReadLog () {
      return this.$store
        .getters['auth/checkPermission'](permissions.LOG_READ);
    },
    canReadProject () {
      return this.$store
        .getters['auth/checkPermission'](permissions.PROJECT_READ);
    },
    canReadWeekends () {
      // everybody can read weekends ?
      return true;
    },
    canReadWorker () {
      return this.$store
        .getters['auth/checkPermission'](permissions.WORKER_READ);
    },
    selectedItem () {
      return this.$route.hash.substring(1);
    },
  },
  methods: {
    formatAdmin (id) {
      if (!id) {
        return 'CLI';
      }

      return (this.mapAdmins[id] || {}).username || id;
    },
    formatAction (type) {
      return mapActionTitle[type];
    },
    formatEntityType (type) {
      return mapEntityTitle[type];
    },
    formatEntityId (type, id) {
      if (!id) {
        return '';
      }

      if (type === entityTypes.admin) {
        return this.formatAdmin(id);
      }
      if (type === entityTypes.department) {
        return (this.mapDepartments[id] || {}).name || id;
      }
      if (type === entityTypes.log) {
        return id;
      }
      if (type === entityTypes.project) {
        return (this.mapProjects[id] || {}).name || id;
      }
      if (type === entityTypes.settings) {
        const mapTitle = {
          'common.orgStructureFile': 'ссылки на оргструктуру',
        };

        return mapTitle[id] || id;
      }
      if (type === entityTypes.weekends) {
        return `${id} год`;
      }
      if (type === entityTypes.worker) {
        const worker = this.mapWorkers[id];
        return worker ? worker.name : id;
      }

      return id;
    },
    buildLinkToAdmin (adminId) {
      if (!adminId) {
        return '';
      }

      return this.canReadAdmin ? `/admins/${adminId}` : '';
    },
    buildLinkToEntity (type, id) {
      if (type === entityTypes.admin) {
        return this.buildLinkToAdmin(id);
      }
      if (type === entityTypes.department) {
        return this.canReadDepartment ? `/departments/${id}` : '';
      }
      if (type === entityTypes.log) {
        return this.canReadLog ? `/logs/${id}` : '';
      }
      if (type === entityTypes.project) {
        return this.canReadProject ? `/projects/${id}` : '';
      }
      if (type === entityTypes.weekends) {
        return this.canReadWeekends ? `/settings/weekends?year=${id}` : '';
      }
      if (type === entityTypes.worker) {
        return this.canReadWorker ? `/workers/${id}` : '';
      }

      return '';
    },
    async fetchJournal () {
      this.isFetching = true;

      try {
        this.items = await service.getList(this.filter);
      } catch (error) {
        console.error(error);
      }

      this.isFetching = false;
      this.$nextTick(this.scrollToSelectedItem);
    },
    handleFilterChange (values) {
      this.filter = values;

      this.fetchJournal();
      this.updateQueryString();
    },
    handleShowDiff (e) {
      const { diff } = e.target.dataset;

      if (!diff) {
        return;
      }

      this.detailsItem = this.items.find(d => d.id === diff);

      if (this.detailsItem) {
        this.$router.replace({
          ...this.$route,
          hash: `#${diff}`,
        });
      }
    },
    handleCloseDiff () {
      this.detailsItem = null;
    },
    updateQueryString () {
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.filter },
      });
    },
    scrollToSelectedItem () {
      if (!this.$el) {
        return;
      }

      const selectedElement = this.$el.querySelector('[data-selected="true"]');

      if (!selectedElement) {
        return;
      }

      const itemRect = selectedElement.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();
      const scrollTo = document.documentElement.scrollTop
        + itemRect.top
        - containerRect.top
        - (containerRect.height / 2);

      document.documentElement.scrollTop = scrollTo;
    },
  },
  created () {
    if (!this.hasReadPermission) {
      return;
    }

    this.filter = { ...this.filter, ...this.$route.query };
    this.fetchJournal();
  },
  mounted () {
    this.$nextTick(this.scrollToSelectedItem);
  },
  render () {
    if (!this.hasReadPermission) {
      return <ErrorAccessDenied />;
    }

    const {
      $style,
      selectedItem,
      formatAdmin,
      formatAction,
      formatEntityType,
      formatEntityId,
      buildLinkToAdmin,
      buildLinkToEntity,
    } = this;

    return (
      <div>
        <header class={$style.header}>
          <u-title>
            Журнал событий
          </u-title>
          <div>
            <DateFilter
              value={this.filter}
              onChange={this.handleFilterChange}
            />
          </div>
        </header>

        <Filter
          class={$style.filter}
          value={this.filter}
          onChange={this.handleFilterChange}
        />

        {this.isFetching ? (
          <u-spinner />
        ) : (
          <u-table onClick={this.handleShowDiff}>
            <u-tr head>
              <u-td width="1">
                Время
              </u-td>
              <u-td width="1">
                Пользователь
              </u-td>
              <u-td>
                Действие
              </u-td>
            </u-tr>
            {this.items.map(item => {
              const linkToAdmin = buildLinkToAdmin(item.adminId);
              const linkToEntity = buildLinkToEntity(
                item.entityType,
                item.entityId,
              );

              return (
                <u-tr
                  key={item.id}
                  class={$style.item}
                  data-selected={String(item.id === selectedItem)}
                >
                  <u-td>
                    <a
                      class={$style.date}
                      href={`#${item.id}`}
                    >
                      {formatDateTime(item.createdAt)}
                    </a>
                  </u-td>
                  <u-td>
                    {linkToAdmin ? (
                      <router-link to={linkToAdmin}>
                        {formatAdmin(item.adminId)}
                      </router-link>
                    ) : formatAdmin(item.adminId)}
                  </u-td>
                  <u-td>
                    {formatAction(item.actionType)}
                    {formatEntityType(item.entityType)}
                    {linkToEntity ? (
                      <router-link to={linkToEntity}>
                        {formatEntityId(item.entityType, item.entityId)}
                      </router-link>
                    ) : formatEntityId(item.entityType, item.entityId)}

                    {Boolean(item.diff) && (
                      <button
                        type="button"
                        class={$style.diffButton}
                        data-diff={item.id}
                      >
                        изменения
                      </button>
                    )}
                  </u-td>
                </u-tr>
              );
            })}
            {!this.items.length && (
              <u-tr>
                <u-td colspan="3">
                  <div class={$style.empty}>
                    <span class={$style.emptyIcon}>
                      <IconGhost />
                    </span>
                    Ничего не найдено
                  </div>
                </u-td>
              </u-tr>
            )}
          </u-table>
        )}

        {Boolean(this.detailsItem) && (
          <ChangesModal
            data={this.detailsItem}
            onClose={this.handleCloseDiff}
          />
        )}
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.header {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
}

.filter {
  margin-bottom: 1em;
}

.empty {
  align-items: center;
  color: $color-gray;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1em;

  &Icon {
    color: $color-gray-lighter;
    font-size: 4em;
  }
}

.date {
  color: $color-black;
  display: inline-block;
  position: relative;
  white-space: nowrap;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.item {
  &:global(:nth-of-type(n)[data-selected='true'] > td.u-table__td) {
    background-color: rgba($color-accent-lightest, .5);
  }
}

.diffButton {
  @include button-reset();

  color: $color-primary;
  margin-left: 1em;
  opacity: 0;

  &:hover,
  &:focus {
    color: $color-primary-lighter;
    text-decoration: underline;
  }
}

.item[data-selected='true'] .diffButton,
.item:hover .diffButton,
.item:focus-within .diffButton {
  opacity: 1;
}
</style>
